import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { AddNewWorkingTimeDialogComponent } from 'src/app/components/main/employees/activity/working-times-history/add-new-working-time-dialog/add-new-working-time-dialog.component';
import { Environment } from 'src/app/models/environment.model';
import { CompanyProductPhase } from 'src/app/models/company_product_phase.model';
import { CompanyProductPhaseBoardCol } from 'src/app/models/company_product_phase_board_col.model';
import { CompanyProductPhaseBoardColRole } from 'src/app/models/company_product_phase_board_col_role.model';
import { ApiService } from 'src/app/services/api.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { KeyValuePipe } from '@angular/common';

@Component({
  selector: 'app-company-product-phase-board-col-role-dialog',
  templateUrl: './company-product-phase-board-col-role-dialog.component.html',
  styleUrls: ['./company-product-phase-board-col-role-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatDialogActions,
    MatButton,
    KeyValuePipe,
  ],
})
export class CompanyProductPhaseBoardColRoleDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<AddNewWorkingTimeDialogComponent>>(MatDialogRef);
  data = inject(MAT_DIALOG_DATA);
  protected fb = inject(UntypedFormBuilder);
  private api = inject(ApiService);

  form: UntypedFormGroup;
  companyProductPhaseBoardCol: CompanyProductPhaseBoardCol = null as any;
  description: string = '';
  companyProductPhaseBoardColRoles: { [key: number]: CompanyProductPhaseBoardColRole } = {};
  environments: Environment[] = null as any;

  private company_product_phase_id: number;
  company_product_phase: CompanyProductPhase = null as any;

  constructor() {
    const data = this.data;

    this.companyProductPhaseBoardCol = data.companyProductPhaseBoardCol;
    this.company_product_phase_id = data.phase_id;

    this.form = this.fb.group({
      id: [data.companyProductPhaseBoardCol.id],
      company_product_phase_board_col_role_id: [data.companyProductPhaseBoardCol.company_product_phase_board_col_role_id],
      environment_id: [data.companyProductPhaseBoardCol.environment_id],
    });
  }

  ngOnInit(): void {
    this.listenToForm();
    this.listenToRoleChanges();
    this.fetchCompanyProductPhase();
    this.fetchColumnRoles();
    this.fetchEnvironments();
  }

  private listenToForm() {
    this.form.valueChanges.subscribe(data => {
      this.updateFormValidation(data);
    });
  }

  private listenToRoleChanges() {
    this.form.controls['company_product_phase_board_col_role_id'].valueChanges.subscribe(val => {
      this.description = this.companyProductPhaseBoardColRoles[val].description;
    });
  }

  private fetchColumnRoles() {
    this.api.getCompanyProductPhaseBoardColRoles().subscribe(res => {
      this.companyProductPhaseBoardColRoles = {};
      res.forEach(role => {
        this.companyProductPhaseBoardColRoles[role.id] = role;
      });

      // force form value change to update description
      this.form.patchValue({
        company_product_phase_board_col_role_id: this.companyProductPhaseBoardCol.company_product_phase_board_col_role_id,
      });
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSubmit() {
    this.api.updateBoardColumn(this.form.value).subscribe(res => {
      this.dialogRef.close(res);
    });
  }

  private fetchEnvironments() {
    this.api.getEnvironments().subscribe(data => {
      this.environments = data;
    });
  }

  private fetchCompanyProductPhase() {
    this.api.getCompanyProductPhase(this.company_product_phase_id).subscribe(data => {
      this.company_product_phase = data;
      if (this.requiresEnvironment(this.form.value)) {
        this.form.controls['environment_id'].addValidators(Validators.required);
      }
    });
  }

  private updateFormValidation(formValue: any) {
    if (this.requiresEnvironment(formValue) && !this.form.controls['environment_id'].hasValidator(Validators.required)) {
      this.form.controls['environment_id'].addValidators(Validators.required);
    } else if (!this.requiresEnvironment(formValue) && this.form.controls['environment_id'].hasValidator(Validators.required)) {
      this.form.controls['environment_id'].removeValidators(Validators.required);
    }
  }

  private requiresEnvironment(formValue: any) {
    return (
      formValue['company_product_phase_board_col_role_id'] != null &&
      Object.keys(this.companyProductPhaseBoardColRoles).length > 0 &&
      ['done', 'test'].indexOf(this.companyProductPhaseBoardColRoles[formValue['company_product_phase_board_col_role_id']].slug) !== -1
    );
  }
}
